import { ErrorBoundary } from '@sentry/react';
import { useSearch } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { useFetchReferencedPosts } from '../hooks/use-fetch-referenced-posts';
import { ReferencedPostsHeader } from './referenced-posts-header';

import { SocialPostsGrid } from '@/features/(insight)/posts/ui/social-post-card/social-posts-grid';
import { SocialPostsGridHeader } from '@/features/(insight)/posts/ui/social-posts-grid-header';
import { ErrorFallback } from '@/shared/components/error-fallback';
import { useCurrentPageRoutingStatus } from '@/shared/hooks/use-is-routing';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { toast } from 'sonner';
import { SORT_OPTION_LIST, type SortOption } from '../../../../entities/post/api/index';

export const CLAIM_MAX_POST_COUNT = 30;

export function ReferencedPostsPage() {
  const fetchEnabled = useCurrentPageRoutingStatus({ currentPath: '/insight/referenced-posts' });
  const { workspaceId } = useWorkspaceId();
  const { session_id, block_id, answer_id, parts, part_id } = useSearch({
    from: '/workspace/$workspaceId/insight/referenced-posts',
  });
  const [order, setOrder] = useState<SortOption>('score');

  const params = useMemo(
    () => ({
      limit: CLAIM_MAX_POST_COUNT,
      order,
      session_id,
      block_id,
      answer_id,
      parts,
      part_id,
    }),
    [session_id, block_id, answer_id, parts, part_id, order],
  );

  const { data, isPending } = useFetchReferencedPosts(params, fetchEnabled);

  // Get total count from API response (available from the first page)
  const totalCount = data?.post_count ?? 0;

  const handleShare = () => {
    const searchParams = new URLSearchParams({
      session_id,
      block_id,
      answer_id,
      parts,
      part_id,
      order,
    });
    navigator.clipboard.writeText(
      `${window.location.origin}/workspace/${workspaceId}/insight/referenced-posts?${searchParams.toString()}`,
    );
    toast.success('Link copied to clipboard');
  };

  return (
    <div className="flex h-full flex-col">
      <ReferencedPostsHeader
        queryName={data?.query_name}
        claimTitle={data?.title}
        isLoading={isPending}
        onShareClick={handleShare}
      />

      <div className="flex-1 overflow-auto">
        <ErrorBoundary
          fallback={({ error, resetError }) => (
            <ErrorFallback error={error as Error} reset={resetError} title="Failed to load posts" />
          )}
        >
          <SocialPostsGridHeader
            type="referenced-posts"
            className="pt-0"
            sortBy={order}
            onSortChange={setOrder}
            totalCount={totalCount}
            params={params}
            isSample={false}
            isLoading={isPending}
            sortOptionList={SORT_OPTION_LIST}
            hideExport
            hideTotalCount
          />
          {totalCount >= CLAIM_MAX_POST_COUNT && (
            <div className="p-6 pb-0">
              <div className="bg-sidebar-accent rounded-md p-1.5 pl-4 text-sm font-medium">
                ✨&nbsp;&nbsp;AI surfaced 30 posts most relevant to this answer.
              </div>
            </div>
          )}
          <SocialPostsGrid
            posts={data?.posts ?? []}
            isLoading={isPending}
            isFetchingNextPage={false}
            hasNextPage={false}
            onLoadMore={() => {}}
          />
        </ErrorBoundary>
      </div>
    </div>
  );
}
